import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  Tab,
  Tabs,
  Card,
  CardContent,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  VpnKey as VpnKeyIcon,
  FamilyRestroom as FamilyRestroomIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  Badge as BadgeIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClientById } from '../redux/slices/clientsSlice';
import { AppDispatch, RootState } from '../redux/store';
import PasswordManagementModal from '../components/PasswordManagementModal';
import FamilyMembersModal from '../components/FamilyMembersModal';
import AnefSyncModal from '../components/AnefSyncModal';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ClientProfileNew: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentClient, loading } = useSelector((state: RootState) => state.clients);

  const [tabValue, setTabValue] = useState(0);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [familyModalOpen, setFamilyModalOpen] = useState(false);
  const [anefSyncModalOpen, setAnefSyncModalOpen] = useState(false);
  const [demarches, setDemarches] = useState<any[]>([]);
  const [loadingDemarches, setLoadingDemarches] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchClientById(parseInt(id)));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentClient) {
      loadDemarches();
    }
  }, [currentClient]);

  const loadDemarches = async () => {
    if (!id) return;

    setLoadingDemarches(true);
    try {
      const response = await api.get(`/demarches?client_id=${id}`);
      if (response.data.success) {
        setDemarches(response.data.data.demarches || []);
      }
    } catch (error) {
      console.error('Erreur chargement démarches:', error);
    } finally {
      setLoadingDemarches(false);
    }
  };

  if (loading || !currentClient) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Chargement...</Typography>
      </Container>
    );
  }

  const clientName = currentClient.type_client === 'entreprise'
    ? (currentClient.raison_sociale || 'Entreprise sans nom')
    : `${currentClient.nom} ${currentClient.prenom || ''}`;

  // Filtrer les démarches en cours
  const demarchesEnCours = demarches.filter(d =>
    !['termine', 'annule'].includes(d.statut)
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/clients')}
          sx={{ mb: 2 }}
        >
          Retour à la liste
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
            <PersonIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom>
              {clientName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={currentClient.type_client}
                color="primary"
                size="small"
              />
              {currentClient.has_family && (
                <Chip
                  icon={<FamilyRestroomIcon />}
                  label="A une famille"
                  color="success"
                  size="small"
                />
              )}
              {currentClient.acces_espace_client && (
                <Chip
                  icon={<VpnKeyIcon />}
                  label="Espace client actif"
                  color="info"
                  size="small"
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={() => setAnefSyncModalOpen(true)}
              color="info"
            >
              Sync ANEF
            </Button>
            <Button
              variant="outlined"
              startIcon={<VpnKeyIcon />}
              onClick={() => setPasswordModalOpen(true)}
            >
              Mots de passe
            </Button>
            <Button
              variant="outlined"
              startIcon={<FamilyRestroomIcon />}
              onClick={() => setFamilyModalOpen(true)}
            >
              Famille
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/clients/edit/${id}`)}
            >
              Modifier
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="fullWidth"
        >
          <Tab label="Informations" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Démarches en cours" icon={<AssignmentIcon />} iconPosition="start" />
          <Tab label={`Toutes les démarches (${demarches.length})`} icon={<DescriptionIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 1: Informations */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Informations personnelles */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  Informations personnelles
                </Typography>
                <Divider sx={{ my: 2 }} />

                <List dense>
                  {currentClient.nom && (
                    <ListItem>
                      <ListItemText primary="Nom" secondary={currentClient.nom} />
                    </ListItem>
                  )}
                  {currentClient.prenom && (
                    <ListItem>
                      <ListItemText primary="Prénom" secondary={currentClient.prenom} />
                    </ListItem>
                  )}
                  {currentClient.sexe && (
                    <ListItem>
                      <ListItemText primary="Sexe" secondary={currentClient.sexe} />
                    </ListItem>
                  )}
                  {currentClient.date_naissance && (
                    <ListItem>
                      <ListItemIcon><CalendarIcon /></ListItemIcon>
                      <ListItemText
                        primary="Date de naissance"
                        secondary={new Date(currentClient.date_naissance).toLocaleDateString('fr-FR')}
                      />
                    </ListItem>
                  )}
                  {currentClient.lieu_naissance && (
                    <ListItem>
                      <ListItemText primary="Lieu de naissance" secondary={currentClient.lieu_naissance} />
                    </ListItem>
                  )}
                  {currentClient.pays_naissance && (
                    <ListItem>
                      <ListItemText primary="Pays de naissance" secondary={currentClient.pays_naissance} />
                    </ListItem>
                  )}
                  {currentClient.situation_familiale && (
                    <ListItem>
                      <ListItemText primary="Situation familiale" secondary={currentClient.situation_familiale} />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon color="primary" />
                  Contact
                </Typography>
                <Divider sx={{ my: 2 }} />

                <List dense>
                  {currentClient.telephone_principal && (
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText
                        primary="Téléphone principal"
                        secondary={
                          <Box component="span">
                            {currentClient.telephone_principal}
                            {currentClient.est_numero_etranger && (
                              <Chip label="Étranger" size="small" sx={{ ml: 1 }} />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  )}
                  {currentClient.telephone_secondaire && (
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText primary="Téléphone secondaire" secondary={currentClient.telephone_secondaire} />
                    </ListItem>
                  )}
                  {currentClient.email && (
                    <ListItem>
                      <ListItemIcon><EmailIcon /></ListItemIcon>
                      <ListItemText primary="Email" secondary={currentClient.email} />
                    </ListItem>
                  )}
                  {currentClient.adresse && (
                    <ListItem>
                      <ListItemIcon><LocationOnIcon /></ListItemIcon>
                      <ListItemText primary="Adresse" secondary={currentClient.adresse} />
                    </ListItem>
                  )}
                  {currentClient.ville && (
                    <ListItem>
                      <ListItemText primary="Ville" secondary={currentClient.ville} />
                    </ListItem>
                  )}
                  {currentClient.pays && (
                    <ListItem>
                      <ListItemText primary="Pays" secondary={currentClient.pays} />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Documents */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon color="primary" />
                  Documents d'identité
                </Typography>
                <Divider sx={{ my: 2 }} />

                <List dense>
                  {currentClient.numero_cni && (
                    <ListItem>
                      <ListItemIcon><BadgeIcon /></ListItemIcon>
                      <ListItemText
                        primary="CNI"
                        secondary={`${currentClient.numero_cni}${currentClient.date_expiration_cni ? ` (Exp: ${new Date(currentClient.date_expiration_cni).toLocaleDateString('fr-FR')})` : ''}`}
                      />
                    </ListItem>
                  )}
                  {currentClient.numero_passeport && (
                    <ListItem>
                      <ListItemIcon><BadgeIcon /></ListItemIcon>
                      <ListItemText
                        primary="Passeport"
                        secondary={`${currentClient.numero_passeport}${currentClient.date_expiration_passeport ? ` (Exp: ${new Date(currentClient.date_expiration_passeport).toLocaleDateString('fr-FR')})` : ''}`}
                      />
                    </ListItem>
                  )}
                  {currentClient.numero_carte_consulaire && (
                    <ListItem>
                      <ListItemIcon><BadgeIcon /></ListItemIcon>
                      <ListItemText
                        primary="Carte consulaire"
                        secondary={`${currentClient.numero_carte_consulaire}${currentClient.date_expiration_carte_consulaire ? ` (Exp: ${new Date(currentClient.date_expiration_carte_consulaire).toLocaleDateString('fr-FR')})` : ''}`}
                      />
                    </ListItem>
                  )}
                  {currentClient.numero_agdref && (
                    <ListItem>
                      <ListItemIcon><BadgeIcon /></ListItemIcon>
                      <ListItemText primary="N° AGDREF" secondary={currentClient.numero_agdref} />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Titre de séjour */}
          {currentClient.titre_sejour && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon color="primary" />
                    Titre de séjour
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <List dense>
                    <ListItem>
                      <ListItemText primary="Titre" secondary={currentClient.titre_sejour} />
                    </ListItem>
                    {currentClient.motif_sejour && (
                      <ListItem>
                        <ListItemText primary="Motif" secondary={currentClient.motif_sejour} />
                      </ListItem>
                    )}
                    {currentClient.date_debut_sejour && (
                      <ListItem>
                        <ListItemText
                          primary="Début de validité"
                          secondary={new Date(currentClient.date_debut_sejour).toLocaleDateString('fr-FR')}
                        />
                      </ListItem>
                    )}
                    {currentClient.date_fin_sejour && (
                      <ListItem>
                        <ListItemText
                          primary="Fin de validité"
                          secondary={new Date(currentClient.date_fin_sejour).toLocaleDateString('fr-FR')}
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Notes */}
          {currentClient.notes && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {currentClient.notes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Tab 2: Démarches en cours */}
      <TabPanel value={tabValue} index={1}>
        {loadingDemarches ? (
          <Typography>Chargement...</Typography>
        ) : demarchesEnCours.length === 0 ? (
          <Alert severity="info">Aucune démarche en cours pour ce client</Alert>
        ) : (
          <Grid container spacing={2}>
            {demarchesEnCours.map((demarche) => (
              <Grid item xs={12} key={demarche.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6">{demarche.titre}</Typography>
                        {demarche.family_member_nom && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Pour: {demarche.family_member_prenom} {demarche.family_member_nom} ({demarche.family_member_relation})
                          </Typography>
                        )}
                        {demarche.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {demarche.description}
                          </Typography>
                        )}
                        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip label={demarche.statut} size="small" color="primary" />
                          {demarche.type_demarche_nom && (
                            <Chip label={demarche.type_demarche_nom} size="small" variant="outlined" />
                          )}
                          {demarche.necessite_intervention && (
                            <Chip label="Intervention requise" size="small" color="warning" />
                          )}
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        onClick={() => navigate(`/demarches/${demarche.id}`)}
                      >
                        Voir détails
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Tab 3: Toutes les démarches */}
      <TabPanel value={tabValue} index={2}>
        {loadingDemarches ? (
          <Typography>Chargement...</Typography>
        ) : demarches.length === 0 ? (
          <Alert severity="info">Aucune démarche pour ce client</Alert>
        ) : (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {demarches.length} démarche{demarches.length > 1 ? 's' : ''} au total
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`${demarchesEnCours.length} en cours`}
                  color="primary"
                  size="small"
                />
                <Chip
                  label={`${demarches.filter(d => d.statut === 'termine').length} terminée(s)`}
                  color="success"
                  size="small"
                />
                <Chip
                  label={`${demarches.filter(d => d.statut === 'annule').length} annulée(s)`}
                  color="default"
                  size="small"
                />
              </Box>
            </Box>

            <Grid container spacing={2}>
              {demarches.map((demarche) => {
                let statusColor: 'default' | 'primary' | 'success' | 'error' | 'warning' = 'default';
                if (demarche.statut === 'termine') statusColor = 'success';
                else if (demarche.statut === 'annule') statusColor = 'default';
                else if (demarche.statut === 'en_cours') statusColor = 'primary';
                else if (demarche.statut === 'en_attente') statusColor = 'warning';

                return (
                  <Grid item xs={12} key={demarche.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="h6">{demarche.titre}</Typography>
                              <Chip label={demarche.statut} size="small" color={statusColor} />
                            </Box>

                            {demarche.family_member_nom && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                👤 Pour: {demarche.family_member_prenom} {demarche.family_member_nom} ({demarche.family_member_relation})
                              </Typography>
                            )}

                            {demarche.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {demarche.description}
                              </Typography>
                            )}

                            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                              {demarche.type_demarche_nom && (
                                <Chip label={demarche.type_demarche_nom} size="small" variant="outlined" />
                              )}
                              {demarche.necessite_intervention && (
                                <Chip label="⚠️ Intervention requise" size="small" color="warning" />
                              )}
                              {demarche.date_depot && (
                                <Typography variant="caption" color="text.secondary">
                                  📅 Déposée le {new Date(demarche.date_depot).toLocaleDateString('fr-FR')}
                                </Typography>
                              )}
                              {demarche.date_limite && (
                                <Typography variant="caption" color="text.secondary">
                                  ⏰ Échéance: {new Date(demarche.date_limite).toLocaleDateString('fr-FR')}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/demarches/${demarche.id}`)}
                          >
                            Détails
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </TabPanel>

      {/* Modals */}
      <PasswordManagementModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        clientId={parseInt(id!)}
        clientName={clientName}
      />

      <FamilyMembersModal
        open={familyModalOpen}
        onClose={() => {
          setFamilyModalOpen(false);
          // Recharger le client pour mettre à jour has_family
          if (id) dispatch(fetchClientById(parseInt(id)));
          // Recharger les démarches pour inclure celles de la famille
          loadDemarches();
        }}
        clientId={parseInt(id!)}
        clientName={clientName}
      />

      <AnefSyncModal
        open={anefSyncModalOpen}
        onClose={() => setAnefSyncModalOpen(false)}
        clientId={parseInt(id!)}
        onSyncSuccess={() => {
          setAnefSyncModalOpen(false);
          // Recharger les démarches après synchronisation
          loadDemarches();
        }}
      />
    </Container>
  );
};

export default ClientProfileNew;
